import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building, Layers, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { UNIT_STATUSES, UNIT_TYPES, shortRupees, unitBatchSchema, unitSchema, type UnitStatus, type UnitType } from '#shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useForm } from '../../lib/form';
import { formatDate } from '../../lib/format';
import type { LeadRow } from '../../lib/leads';
import { LeadPicker } from '../../components/leads/LeadPicker';
import { Badge, Button, Card, ConfirmDialog, Drawer, EmptyState, ErrorState, PageHeader, SearchInput, SelectField, TableSkeleton, TextAreaField, TextField, cx, inlineInputClass } from '../../components/ui';
import { LeadLink } from '../../lib/links';

export interface Unit {
  id: string; project: string; code: string; unitType: UnitType; bedrooms: number | null; sizeSqft: number | null; block: string | null; floor: string | null;
  facing: string | null; price: number | null; status: UnitStatus; blockedUntil: string | null; soldAt: string | null; notes: string | null;
  leadId: string | null; leadName: string | null; leadNumber: number | null; leadOwnerId: string | null; leadOwnerName: string | null;
}
interface ProjectSummary { project: string; total: number; available: number; blocked: number; sold: number; soldValue: number }

const STATUS = Object.fromEntries(UNIT_STATUSES.map((s) => [s.key, s])) as Record<UnitStatus, (typeof UNIT_STATUSES)[number]>;
const TILE: Record<UnitStatus, string> = {
  available: 'bg-emerald-50 ring-emerald-300 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:ring-emerald-800',
  blocked: 'bg-amber-50 ring-amber-300 hover:bg-amber-100 dark:bg-amber-950/40 dark:ring-amber-800',
  sold: 'bg-indigo-50 ring-indigo-300 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:ring-indigo-800',
  inactive: 'bg-slate-100 ring-slate-300 hover:bg-slate-200 dark:bg-slate-800 dark:ring-slate-700',
};
const typeLabel = (t: UnitType) => UNIT_TYPES.find((x) => x.key === t)?.label ?? t;
export const unitSummary = (u: Pick<Unit, 'unitType' | 'bedrooms' | 'sizeSqft'>) =>
  [u.bedrooms !== null && u.unitType !== 'plot' ? `${u.bedrooms} BHK` : null, u.unitType !== 'flat' ? typeLabel(u.unitType) : null, u.sizeSqft ? `${u.sizeSqft.toLocaleString('en-IN')} sq.ft` : null].filter(Boolean).join(' · ');

export default function Inventory() {
  const { can } = useAuth();
  const manage = can('inventory.manage');
  const [project, setProject] = useState('');
  const [status, setStatus] = useState<UnitStatus | ''>('');
  const [bhk, setBhk] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [q, setQ] = useState('');
  const params = new URLSearchParams(Object.entries({ project, status, bedrooms: bhk, maxPrice: maxPrice ? String(Number(maxPrice) * 100000) : '', q }).filter(([, v]) => v));
  const list = useQuery({ queryKey: ['inventory', params.toString()], queryFn: () => api.get<{ items: Unit[]; projects: ProjectSummary[] }>(`/inventory?${params}`) });
  const [open, setOpen] = useState<Unit | null>(null);
  const [adding, setAdding] = useState<'one' | 'many' | null>(null);

  // Tiles grouped by project, then block / tower.
  const groups = useMemo(() => {
    const out = new Map<string, Map<string, Unit[]>>();
    for (const u of list.data?.items ?? []) {
      const p = out.get(u.project) ?? new Map<string, Unit[]>();
      const key = u.block ?? '';
      p.set(key, [...(p.get(key) ?? []), u]);
      out.set(u.project, p);
    }
    return [...out.entries()];
  }, [list.data]);
  const projects = list.data?.projects ?? [];

  return (
    <>
      <PageHeader title="Inventory" description="Every flat, plot or villa you sell — what’s free, what’s on hold for a customer, and what’s sold."
        actions={manage && <>
          <Button variant="secondary" icon={<Plus className="size-4" />} onClick={() => setAdding('one')}>Add a unit</Button>
          <Button icon={<Layers className="size-4" />} onClick={() => setAdding('many')}>Add many units</Button>
        </>} />

      {projects.length > 0 && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <button key={p.project} onClick={() => setProject(project === p.project ? '' : p.project)}
              className={cx('rounded-xl bg-white p-4 text-left ring-1 ring-slate-200 transition hover:ring-brand-400 dark:bg-slate-900 dark:ring-slate-800', project === p.project && 'ring-2 ring-brand-500')}>
              <p className="flex items-center gap-2 font-semibold"><Building className="size-4 text-slate-400" />{p.project}</p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <span className="text-emerald-700 dark:text-emerald-400"><b>{p.available}</b> available</span>
                <span className="text-amber-700 dark:text-amber-400"><b>{p.blocked}</b> on hold</span>
                <span className="text-indigo-700 dark:text-indigo-400"><b>{p.sold}</b> sold</span>
                <span className="text-slate-500">of {p.total}</span>
              </div>
              {p.soldValue > 0 && <p className="mt-1 text-xs text-slate-500">Sold value {shortRupees(p.soldValue)}</p>}
            </button>
          ))}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4 dark:border-slate-800">
          <div className="w-full sm:w-56"><SearchInput value={q} onChange={setQ} placeholder="Unit number or customer" /></div>
          <div className="flex flex-wrap gap-1.5">
            {[{ key: '', label: 'All' }, ...UNIT_STATUSES].map((s) => (
              <button key={s.key} onClick={() => setStatus(s.key as UnitStatus | '')}
                className={cx('rounded-full px-3 py-1.5 text-sm ring-1', status === s.key ? 'bg-brand-600 text-white ring-brand-600' : 'ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800')}>{s.label}</button>
            ))}
          </div>
          <select aria-label="Bedrooms" value={bhk} onChange={(e) => setBhk(e.target.value)} className={inlineInputClass}>
            <option value="">Any size</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} BHK</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">Budget up to
            <input inputMode="numeric" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ''))} placeholder="Any" className={cx(inlineInputClass, 'w-20')} /> lakh
          </label>
        </div>

        {list.isPending ? <TableSkeleton /> : list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : list.data.items.length === 0 ? (
          projects.length === 0
            ? <EmptyState icon={<Building className="size-6" />} title="No units yet" description={manage ? 'Add your project’s flats or plots — “Add many units” creates A-101 to A-120 in one go.' : 'Your manager hasn’t added any units yet.'}
                action={manage && <Button icon={<Layers className="size-4" />} onClick={() => setAdding('many')}>Add many units</Button>} />
            : <EmptyState icon={<Building className="size-6" />} title="Nothing matches" description="Try a different status, size or budget." />
        ) : (
          <div className="space-y-6 p-4">
            {groups.map(([proj, blocks]) => (
              <section key={proj}>
                <h2 className="mb-2 font-semibold">{proj}</h2>
                {[...blocks.entries()].map(([block, units]) => (
                  <div key={block} className="mb-3">
                    {block && <p className="mb-1.5 text-xs font-medium tracking-wide text-slate-500 uppercase">{block}</p>}
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-2">
                      {units.map((u) => (
                        <button key={u.id} onClick={() => setOpen(u)} className={cx('rounded-lg p-2.5 text-left ring-1 transition', TILE[u.status])}>
                          <p className="font-semibold">{u.code}</p>
                          <p className="truncate text-xs text-slate-600 dark:text-slate-400">{[u.bedrooms !== null && u.unitType !== 'plot' ? `${u.bedrooms} BHK` : typeLabel(u.unitType), u.sizeSqft && `${u.sizeSqft.toLocaleString('en-IN')} ft²`].filter(Boolean).join(' · ')}</p>
                          <p className="text-sm font-medium">{shortRupees(u.price)}</p>
                          <p className="truncate text-xs" style={{ color: STATUS[u.status].color }}>{u.status === 'available' ? 'Available' : u.status === 'inactive' ? 'Not for sale' : u.leadName ?? u.leadOwnerName ?? STATUS[u.status].label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </Card>

      {open && <UnitDrawer unit={open} onClose={() => setOpen(null)} />}
      {adding === 'one' && <UnitForm onClose={() => setAdding(null)} defaultProject={project} />}
      {adding === 'many' && <BatchForm onClose={() => setAdding(null)} defaultProject={project} />}
    </>
  );
}

function UnitDrawer({ unit, onClose }: { unit: Unit; onClose: () => void }) {
  const { can, me } = useAuth();
  const qc = useQueryClient();
  const manage = can('inventory.manage');
  const [mode, setMode] = useState<'view' | 'hold' | 'sell' | 'edit'>('view');
  const [lead, setLead] = useState<LeadRow | null>(null);
  const [days, setDays] = useState(7);
  const [price, setPrice] = useState(unit.price ? String(unit.price) : '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = async (msg: string) => { toast.success(msg); await qc.invalidateQueries({ queryKey: ['inventory'] }); await qc.invalidateQueries({ queryKey: ['lead-units'] }); onClose(); };
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onError: (e) => toast.error(e.message),
  });
  const heldByMe = unit.status === 'blocked' && unit.leadOwnerId === me?.id;
  const canRelease = unit.status === 'blocked' && (manage || (heldByMe && can('leads.edit')) || (!!unit.leadId && can('leads.edit')));

  if (mode === 'edit') return <UnitForm unit={unit} onClose={onClose} />;
  return (
    <Drawer open onClose={onClose} title={`${unit.code} · ${unit.project}`} description={unitSummary(unit)}
      footer={mode === 'view' ? <>
        {manage && unit.status !== 'sold' && <Button variant="ghost" className="mr-auto text-red-600" onClick={() => setConfirmDelete(true)}>Delete</Button>}
        {manage && <Button variant="secondary" onClick={() => setMode('edit')}>Edit</Button>}
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </> : <>
        <Button variant="secondary" onClick={() => setMode('view')}>Back</Button>
        {mode === 'hold' && <Button disabled={!lead} loading={act.isPending} onClick={() => act.mutate(async () => { await api.post(`/inventory/${unit.id}/hold`, { leadId: lead!.id, days }); await done(`${unit.code} is on hold for ${lead!.name} for ${days} days.`); })}>Put on hold</Button>}
        {mode === 'sell' && <Button disabled={!lead} loading={act.isPending} onClick={() => act.mutate(async () => { await api.post(`/inventory/${unit.id}/sell`, { leadId: lead!.id, price: price || undefined }); await done(`${unit.code} marked sold to ${lead!.name}. Congratulations! 🎉`); })}>Mark sold</Button>}
      </>}>
      {mode === 'view' && (
        <div className="space-y-5">
          <div className="flex items-center gap-2"><Badge tone={unit.status === 'available' ? 'green' : unit.status === 'blocked' ? 'amber' : unit.status === 'sold' ? 'brand' : 'gray'}>{STATUS[unit.status].label}</Badge>
            <span className="text-2xl font-semibold">{shortRupees(unit.price)}</span></div>
          {unit.status === 'blocked' && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm dark:bg-amber-950/40">
              On hold {unit.leadName && unit.leadId ? <>for <LeadLink id={unit.leadId} number={unit.leadNumber} name={unit.leadName} /></> : 'for a customer'}{unit.leadOwnerName ? ` (${unit.leadOwnerName})` : ''} until {formatDate(unit.blockedUntil)}.
            </p>
          )}
          {unit.status === 'sold' && <p className="rounded-lg bg-indigo-50 p-3 text-sm dark:bg-indigo-950/40">Sold {unit.leadName && unit.leadId ? <>to <LeadLink id={unit.leadId} number={unit.leadNumber} name={unit.leadName} /></> : ''} on {formatDate(unit.soldAt)}.</p>}
          <dl className="grid grid-cols-2 gap-3 text-sm">
            {([['Type', typeLabel(unit.unitType)], ['Bedrooms', unit.bedrooms !== null ? `${unit.bedrooms} BHK` : null], ['Size', unit.sizeSqft ? `${unit.sizeSqft.toLocaleString('en-IN')} sq.ft` : null],
              ['Block / tower', unit.block], ['Floor', unit.floor], ['Facing', unit.facing]] as const).filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><dt className="text-slate-500">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
          </dl>
          {unit.notes && <p className="text-sm whitespace-pre-line text-slate-600 dark:text-slate-400">{unit.notes}</p>}
          <div className="flex flex-wrap gap-2">
            {unit.status === 'available' && can('leads.edit') && <Button onClick={() => setMode('hold')}>Hold for a customer</Button>}
            {(unit.status === 'available' || unit.status === 'blocked') && (manage || heldByMe) && (
              <Button variant={unit.status === 'blocked' ? 'primary' : 'secondary'} onClick={() => { setMode('sell'); if (heldByMe && unit.leadId) setLead({ id: unit.leadId, number: unit.leadNumber, name: unit.leadName } as LeadRow); }}>Mark sold</Button>
            )}
            {canRelease && <Button variant="secondary" loading={act.isPending} onClick={() => act.mutate(async () => { await api.post(`/inventory/${unit.id}/release`); await done(`${unit.code} is available again.`); })}>Release hold</Button>}
            {manage && (unit.status === 'available' || unit.status === 'inactive') && (
              <Button variant="ghost" onClick={() => act.mutate(async () => { await api.post(`/inventory/${unit.id}/active`, { active: unit.status === 'inactive' }); await done(unit.status === 'inactive' ? `${unit.code} is for sale again.` : `${unit.code} taken off the market.`); })}>
                {unit.status === 'inactive' ? 'Put back on sale' : 'Take off the market'}
              </Button>
            )}
          </div>
        </div>
      )}
      {mode === 'hold' && (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 dark:text-slate-400">Keeps {unit.code} aside so nobody else sells it. It becomes available again by itself if you don’t complete the sale.</p>
          <div><p className="mb-1.5 text-sm font-medium">For which customer?</p><LeadPicker value={lead} onChange={setLead} /></div>
          <SelectField label="Hold for" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 2, 3, 7, 15, 30].map((d) => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
          </SelectField>
        </div>
      )}
      {mode === 'sell' && (
        <div className="space-y-5">
          <div><p className="mb-1.5 text-sm font-medium">Sold to</p>{heldByMe && !manage ? <p className="font-medium">L-{unit.leadNumber} {unit.leadName}</p> : <LeadPicker value={lead} onChange={setLead} />}</div>
          <TextField label="Final price (₹)" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} hint={price ? shortRupees(Number(price)) : 'Leave as it is if the price didn’t change.'} />
        </div>
      )}
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} danger title={`Delete ${unit.code}?`} message="It will be removed from your inventory." confirmLabel="Delete"
        loading={act.isPending} onConfirm={() => act.mutate(async () => { await api.del(`/inventory/${unit.id}`); await done(`${unit.code} deleted.`); })} />
    </Drawer>
  );
}

function UnitFields({ form }: { form: ReturnType<typeof useForm<typeof unitSchema>> | ReturnType<typeof useForm<typeof unitBatchSchema>> }) {
  const f = form as ReturnType<typeof useForm<typeof unitSchema>>;
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField label="Type" {...f.bind('unitType')}>{UNIT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</SelectField>
        <SelectField label="Bedrooms" value={String(f.values.bedrooms ?? '')} onChange={(e) => f.set('bedrooms', e.target.value === '' ? undefined : Number(e.target.value))}>
          <option value="">—</option>{[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} BHK</option>)}
        </SelectField>
        <TextField label="Size (sq.ft)" inputMode="decimal" {...f.bind('sizeSqft')} />
        <TextField label="Price (₹)" inputMode="numeric" {...f.bind('price')} hint={f.values.price ? shortRupees(Number(String(f.values.price).replace(/[,₹\s]/g, ''))) : 'e.g. 4500000 for 45 lakh'} />
        <TextField label="Block / tower (optional)" {...f.bind('block')} placeholder="e.g. Tower A" />
        <TextField label="Facing (optional)" {...f.bind('facing')} placeholder="e.g. East" />
      </div>
      <TextAreaField label="Notes (optional)" {...f.bind('notes')} placeholder="Corner unit, park view, 2 car parks…" />
    </>
  );
}

function UnitForm({ unit, onClose, defaultProject = '' }: { unit?: Unit; onClose: () => void; defaultProject?: string }) {
  const qc = useQueryClient();
  const form = useForm(unitSchema, {
    project: unit?.project ?? defaultProject, code: unit?.code ?? '', unitType: unit?.unitType ?? 'flat', bedrooms: unit?.bedrooms ?? undefined,
    sizeSqft: unit?.sizeSqft ?? '', block: unit?.block ?? '', floor: unit?.floor ?? '', facing: unit?.facing ?? '', price: unit?.price ?? '', notes: unit?.notes ?? '',
  });
  const save = () => form.submit(async (data) => {
    if (unit) await api.patch(`/inventory/${unit.id}`, data); else await api.post('/inventory', data);
    toast.success(unit ? 'Unit saved.' : `${data.code} added.`);
    await qc.invalidateQueries({ queryKey: ['inventory'] });
    onClose();
  });
  return (
    <Drawer open onClose={onClose} title={unit ? `Edit ${unit.code}` : 'Add a unit'} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} loading={form.submitting}>Save</Button></>}>
      <form className="space-y-5" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField label="Project" autoFocus={!unit} {...form.bind('project')} placeholder="e.g. Green Meadows" />
          <TextField label="Unit number" {...form.bind('code')} placeholder="e.g. A-101 or Plot 27" />
          <TextField label="Floor (optional)" {...form.bind('floor')} />
        </div>
        <UnitFields form={form} />
        <button type="submit" hidden />
      </form>
    </Drawer>
  );
}

function BatchForm({ onClose, defaultProject = '' }: { onClose: () => void; defaultProject?: string }) {
  const qc = useQueryClient();
  const form = useForm(unitBatchSchema, { project: defaultProject, prefix: 'A-', from: 101, to: 110, unitType: 'flat', bedrooms: 2, sizeSqft: '', block: '', floor: '', facing: '', price: '', notes: '' });
  const v = form.values;
  const count = Math.max(0, Number(v.to) - Number(v.from) + 1);
  const save = () => form.submit(async (data) => {
    const r = await api.post<{ created: number; skipped: number }>('/inventory/batch', data);
    toast.success(`${r.created} unit${r.created === 1 ? '' : 's'} added${r.skipped ? ` (${r.skipped} already existed)` : ''}.`);
    await qc.invalidateQueries({ queryKey: ['inventory'] });
    onClose();
  });
  return (
    <Drawer open onClose={onClose} title="Add many units" description="Same details for a run of numbers — you can edit any unit later."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} loading={form.submitting}>Add {count || ''} units</Button></>}>
      <form className="space-y-5" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <TextField label="Project" autoFocus {...form.bind('project')} placeholder="e.g. Green Meadows" />
        <div className="grid grid-cols-3 gap-3">
          <TextField label="Starts with" {...form.bind('prefix')} placeholder="A-" />
          <TextField label="From" inputMode="numeric" {...form.bind('from')} />
          <TextField label="To" inputMode="numeric" {...form.bind('to')} />
        </div>
        {count > 0 && count <= 500 && <p className="-mt-2 text-sm text-slate-500">Creates {`${v.prefix ?? ""}${String(v.from)}, ${v.prefix ?? ""}${Number(v.from) + 1} … ${v.prefix ?? ""}${String(v.to)}`} ({count} units).</p>}
        <UnitFields form={form} />
        <button type="submit" hidden />
      </form>
    </Drawer>
  );
}
